<template>

<div class="order-summary text-center" v-if="parsedOrder" :key="parsedOrder.orderHash">

	<div :class="{ 'order-section-plain' : plain, 'order-section shadow-box': !plain }">

		<!-- Begin maker and taker token -->
		<v-row dense>
			<v-col>
				<!-- maker token -->
				<div class="nowrap">
					<ERC20Token :symbol="parsedOrder.metaData.makerAsset.symbol"
						:name="parsedOrder.metaData.makerAsset.name"
						:address="parsedOrder.makerAssetAddress"
						:decimals="parsedOrder.metaData.makerAsset.decimals"
						:tokenurl="tokenurl" />
					<div class="order-amount-wrapper">{{ $t('common.verb.sell') }}: <AmountNumber :value="parsedOrder.makerAssetAmount" /></div>
				</div>
			</v-col>

			<v-col>
				<!-- taker token -->
				<div class="nowrap">
					<ERC20Token :symbol="parsedOrder.metaData.takerAsset.symbol"
						:name="parsedOrder.metaData.takerAsset.name"
						:address="parsedOrder.takerAssetAddress"
						:decimals="parsedOrder.metaData.takerAsset.decimals"
						:tokenurl="tokenurl" />
					<div class="order-amount-wrapper">{{ $t('common.verb.buy') }}: <AmountNumber :value="parsedOrder.takerAssetAmount" /></div>
				</div>
			</v-col>
		</v-row>
		<!-- End maker and taker token -->

		<!-- Begin order pice -->
		<v-row dense>
			<!-- Price: taker per maker -->
			<v-col>
				<div class="nowrap">
          1 {{ parsedOrder.metaData.makerAsset.symbol }}
          = <strong>{{ formatAmountNumber(parsedOrder.priceTakerPerMaker) }}</strong>
          {{ parsedOrder.metaData.takerAsset.symbol }}
        </div>
			</v-col>

			<!-- Price: maker per taker -->
			<v-col>
				<div class="nowrap">
          1 {{ parsedOrder.metaData.takerAsset.symbol }}
          = <strong>{{ formatAmountNumber(parsedOrder.priceMakerPerTaker) }}</strong>
          {{ parsedOrder.metaData.makerAsset.symbol }}
        </div>
			</v-col>
		</v-row>
		<!-- End order pice -->

		<!-- Begin maker and taker fees -->
		<v-row dense>
			<!-- maker fee -->
			<v-col cols="6" class="nowrap">
				<strong>{{ $t('common.noun.makerFee') }}</strong>
				<span v-if="userIsMaker"> ({{ $t('compOrderSummary.youPay') }}): </span>
				<span v-else> ({{ $t('compOrderSummary.makerPay') }}): </span>
				<AmountNumber :value="parsedOrder.makerFee" />
				<span v-if="parsedOrder.makerFee > 0">
					{{ parsedOrder.metaData.makerFeeAsset.symbol }}
				</span>
			</v-col>

			<!-- taker fee -->
			<v-col cols="6" class="nowrap">
				<strong>{{ $t('common.noun.takerFee') }}</strong>
				<span v-if="userIsTaker"> ({{ $t('compOrderSummary.youPay') }}): </span>
				<span v-else> ({{ $t('compOrderSummary.takerPay') }}): </span>
				<AmountNumber :value="parsedOrder.takerFee" />
				<span v-if="parsedOrder.takerFee > 0">
					{{ parsedOrder.metaData.takerFeeAsset.symbol }}
				</span>
			</v-col>

			<!-- Protocol Fee -->
			<v-col cols="12" class="nowrap" v-if="estimatedProtocolFee">
				<strong>{{ $t('compOrderSummary.estimatedProtocolFee') }}</strong>
				<span v-if="userIsTaker"> ({{ $t('compOrderSummary.youPay') }}): </span>
				<span v-else> ({{ $t('compOrderSummary.takerPay') }}): </span>
				<AmountNumber :value="estimatedProtocolFee" /> ETH
			</v-col>
		</v-row>
		<!-- End maker and taker fees -->

		<!-- Begin maker and taker address -->
		<v-row dense>
			<!-- maker -->
			<v-col>
				<strong>{{ $t('common.noun.maker') }}</strong>: <UserAddress :address="parsedOrder.makerAddress" :myaddress="myaddress" :accounturl="accounturl" markself></UserAddress>
			</v-col>

			<!-- taker -->
			<v-col v-if="orderHasTaker">
				<strong>{{ $t('common.noun.taker') }}</strong>: <UserAddress :address="parsedOrder.takerAddress" :myaddress="myaddress" :accounturl="accounturl" markself></UserAddress>
			</v-col>
		</v-row>
		<!-- End maker and taker address -->

		<!-- Begin expiration time -->
		<v-row dense>
			<v-col>
				<strong>{{ $t('common.orderForm.label.expirationTime') }}</strong> {{ showDateTime(parsedOrder.expirationTimeSeconds * 1000) }}
			</v-col>
		</v-row>
		<!-- Begin expiration time -->

		<!-- Begin relayer -->
		<v-row dense v-if="showrelayer && relayer">
			<v-col>
				<strong>{{ $t('common.noun.relayer') }}</strong>: {{ relayer.name }}
			</v-col>
		</v-row>
		<!-- Begin relayer -->


		<div v-if="!viewonly" v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="pa-2 mt-1 mb-2">
			<!-- Begin fillable status -->
			<v-row dense>
				<v-col>

				<strong>{{ $t('common.noun.fillable') }}</strong>:

				<span v-if="cancelOrderLoading">
					<ButtonLoading size="20" color="grey" class="mr-3" />
					{{ $t('compOrderSummary.cancelOrder.cancelLoading') }}...
				</span>
				<span v-else-if="orderStateLoading">
					<ButtonLoading size="20" color="grey" class="mr-3" />
				</span>
				<span v-else-if="orderState.marketFillable">
					<v-icon color="success">mdi-check-circle</v-icon>
				</span>
				<span v-else>
					<v-icon color="red">mdi-close-circle</v-icon>
				</span>
				<v-btn v-if="!initialOrderStateLoadingDone" x-small color="secondary" @click="checkOrderState" :disabled="orderStateLoading" :loading="orderStateLoading" class="ml-2">
					{{ $t('common.verb.check') }}
				</v-btn>
				<v-btn v-if="orderState.marketFillable && !continuousorderstatechecking" x-small color="secondary"
					@click="checkOrderState" :disabled="orderStateLoading" :loading="orderStateLoading" class="ml-2">
					{{ $t('common.verb.checkAgain') }}
				</v-btn>
				</v-col>
			</v-row>
			<!-- End fillable status -->

			<!-- Begin Status for Amounts Filled and Available -->
			<v-row dense v-if="!viewonly && initialOrderStateLoadingDone">
				<v-col>
					<div class="nowrap">
						{{ $t('compOrderSummary.tokenAmountFilled', { tokenSymbol: parsedOrder.metaData.takerAsset.symbol }) }}:
						<strong>{{ formatAmountNumber(orderState.filledTakerAmount) }}</strong>
					</div>
				</v-col>
				<v-col>
					<div class="nowrap">
						{{ $t('compOrderSummary.tokenAmountAvailable', { tokenSymbol: parsedOrder.metaData.takerAsset.symbol }) }}:
						<strong>{{ formatAmountNumber(orderState.availableTakerAmount) }}</strong>
					</div>
				</v-col>
			</v-row>
			<!-- Begin Status for Amounts Filled and Available -->
		</div>

		<!-- Begin Fill Order Form -->
		<div v-if="!viewonly && showfillorder && !isMyOwnOrder && initialOrderStateLoadingDone">

			<!-- order status details -->
			<div v-if="orderState.selfFillable === false || orderState.marketFillable === false" class="mt-2">
				<div v-bind:class="{'red lighten-4': !$vuetify.theme.dark, 'deep-orange darken-4': $vuetify.theme.dark }" class="pa-2">
					<div class="title text-center mt-1 mb-2">{{ $t('compOrderSummary.youCannotFill') }}</div>
					<ul class="mb-3">
					<li v-for="reason of orderState.unfillableReasons" :key="reason">{{ $t('compOrderSummary.unfillableReason.' + reason, { tokenSymbol: parsedOrder.metaData.takerAsset.symbol }) }}</li>
					</ul>
				</div>
			</div>

			<div v-else v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="py-1 px-2 mt-3">
				<div class="title text-center mt-2 mb-0">{{ $t('common.phrase.fillOrder') }}</div>
				<div class="px-3 pt-0">

					<FillOrderForm
						:ismodal="ismodal"
						:parsedOrder="parsedOrder"
						:orderState="orderState"
						:myaccount="myaccount"
						:myaddress="myaddress"
						:gas-price="gasPrice"
						v-on:check-order-state-request="checkOrderState()"
						v-on:fill-order-form-close-modal="emitFillOrderCloseModal"
					>
					</FillOrderForm>

				</div>
			</div>

		</div>
		<!-- END Fill Order Form -->

		<!-- Begin Forwarder Buy Form -->
<!-- 		<div v-if="isLocalHost && !viewonly && showfillorder &&!isMyOwnOrder && initialOrderStateLoadingDone && orderState.forwarderBuyPossible">

			<div class="bg-green py-1 px-2 mt-3">

				<div class="section-title text-center mt-2 mb-0">Buy {{ parsedOrder.metaData.makerAsset.symbol }} with Ether</div>

				<div class="px-3 pt-0">

					<ForwarderBuyERC20Form
						:parsedOrder="parsedOrder"
						:orderState="orderState"
						:myaccount="myaccount"
						:myaddress="myaddress"
						v-on:check-order-state-request="checkOrderState()"
					>
					</ForwarderBuyERC20Form>

				</div>

			</div>

		</div> -->
		<!-- END Forwarder Buy Form -->

		<!-- cancel order and delete order links -->
		<div v-if="!viewonly && showorderlink || showdeletelink" class="text-right">

			<span v-if="initialOrderStateLoadingDone && showdeletelink && myaddress && parsedOrder.signedOrder.makerAddress === myaddress">
					&nbsp;&nbsp;
					<v-btn v-if="orderState.marketFillable === true" @click="showCancelOrderModal"
						color="error" small :disabled="cancelOrderLoading">
						{{ $t('common.phrase.cancelOrder') }}
						<ButtonLoading class="mr-3" v-show="cancelOrderLoading" />
					</v-btn>
					<v-btn v-else @click="showDeleteOrderModal" color="error" small>
						{{ $t('common.phrase.deleteOrderRecord') }}
					</v-btn>
			</span>

			<!-- view order link -->
			<span v-if="showorderlink" class="ml-3">
				<v-btn color="secondary" small :to="{ name: 'OrderDetails', params: { orderHash: parsedOrder.orderHash } }">{{ $t('common.phrase.viewOrder') }}</v-btn>
			</span>

		</div>

	</div><!-- order summary -->

	<CancelOrderModal v-if="isMyOwnOrder"
		:ref="'cancel-order-modal-' + parsedOrder.orderHash"
		:parsedOrder="parsedOrder"
		v-on:cancel-order-failure="handleCancelOrderFailure()"
		v-on:cancel-order-loading="handleCancelOrderLoading()"
		v-on:cancel-order-complete="handleCancelOrderDone()"
	></CancelOrderModal>

	<DeleteOrderModal v-if="isMyOwnOrder"
    :ref="'delete-order-modal-' + parsedOrder.orderHash"
    :parsedOrder="parsedOrder"
    v-on:delete-order-complete="emitDeleteOrderComplete()"
  ></DeleteOrderModal>

</div>

</template>

<script>

import { config } from '@/config'
import Preloader from "@/components/Preloader"
import UserAddress from "@/components/UserAddress"
import AmountNumber from "@/components/AmountNumber"
import { ERC20TokenContract } from '@0x/contract-wrappers'
import { BigNumber } from '@0x/utils'
import { Web3Wrapper } from '@0x/web3-wrapper'
import PendingTransaction from "@/components/PendingTransaction"
import ERC20Token from "@/components/ERC20Token"
import {mapActions, mapGetters, mapState} from 'vuex'
import { NULL_ADDRESS, ZERO, TX_DEFAULTS } from '@/config/constants'
import ButtonLoading from "@/components/ButtonLoading"
import { parseOrderJsonCode, showDateTime, formatAmountNumber, formatAllowanceNumber, countDecimals, loadOrderState, calculateProtocolFee, getCurrentGasPrice } from '@/utils/utils'
import FillOrderForm from "@/components/FillOrderForm"
import CancelOrderModal from "@/components/CancelOrderModal"
import DeleteOrderModal from "@/components/DeleteOrderModal"

const loopJobInternval = 1000 * 10

export default {
	name: 'OrderSummary',
	components: {
		UserAddress,
		AmountNumber,
		ERC20Token,
		ButtonLoading,
		FillOrderForm,
		// ForwarderBuyERC20Form,
		PendingTransaction,
		CancelOrderModal,
		DeleteOrderModal,
	},
	props: {
		parsedOrder: { type: Object, required: true },
		myaddress: { type: String, required: true },
		myaccount: { type: Object, required: true },
		accounturl: { type: String },
		txurl: { type: String },
		tokenurl: { type: String },
		relayer: { type: Object },
		initialorderstatechecking: { type: Boolean, default: false },
		continuousorderstatechecking: { type: Boolean, default: false },
		showorderlink: { type: Boolean, default: false },
		showdeletelink: { type: Boolean, default: false },
		showfillorder: { type: Boolean, default: false },
		showrelayer: { type: Boolean, default: false },
		plain: { type: Boolean, default: false },
		ismodal: { type: Boolean, default: false },
		viewonly: { type: Boolean, default: false },
		takerView: { type: Boolean, default: false },
	},
	data () {
		return {
			orderState: {},
			checking: false,
			initialOrderStateLoadingDone: false,
			orderStateLoading: false,
			cancelOrderLoading: false,
			showJsonCode: false,
			gasPrice: null,
			loopJob: null
		}
	},
	computed: {
		...mapGetters({
			network: 'getNetwork',
			account: 'getAccount',
			tokenInfo: 'getTokenInfo',
			myAddress: 'getUserAccount',
			networkConfig: 'getNetworkConfig',
			web3Wrapper: 'getWeb3Wrapper',
			providerEngine: 'getProviderEngine',
			contractWrappers: 'getContractWrappers',
			contractAddresses: 'getContractAddresses',
		}),
		currentURL: function() {
			return window.location.href
		},
		ZRXToken: function() {
			return this.tokenInfo.ZRXToken
		},
		WETHToken: function() {
			return this.tokenInfo.WETHToken
		},
		orderHasTaker: function() {
			return (this.parsedOrder.takerAddress && this.parsedOrder.takerAddress != NULL_ADDRESS)
		},
		isMyOwnOrder: function () {
			return this.parsedOrder.makerAddress && this.parsedOrder.makerAddress === this.myaddress
		},
		userIsMaker: function () {
			return this.parsedOrder.makerAddress && this.parsedOrder.makerAddress === this.myaddress
		},
		userIsTaker: function () {
			return this.takerView || (this.parsedOrder.takerAddress && this.parsedOrder.takerAddress === this.myaddress)
		},
		orderAvailableTakerAmountNotZero: function () {
			return Number.parseFloat(this.orderState.availableTakerAmount) > this.config.minFillableAmount
		},
		isLocalHost: function() {
			return window.location.href.includes('localhost')
		},
		canDoForwarderBuy: function() {
			return this.parsedOrder && this.tokenInfo.WETHToken && this.parsedOrder.takerAssetAddress === this.tokenInfo.WETHToken.address
		},
		estimatedProtocolFee: function() {
			if (this.parsedOrder && this.gasPrice) {
				const fee = calculateProtocolFee([this.parsedOrder], this.gasPrice)
				const feeDisplay = Web3Wrapper.toUnitAmount(fee, this.tokenInfo.WETHToken.decimals).toNumber()
				return feeDisplay
			}
			else {
				return null
			}
		},
	},
	methods: {
		...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount', 'updateAccountForToken' ]),
		showDateTime: showDateTime,
		async init() {
			// await this.loadNetwork()
			// console.log(this.parsedOrder)
			// console.log(this.network)
			this.orderStateLoading = false
			this.initialOrderStateLoadingDone = false

			this.gasPrice = await getCurrentGasPrice(this.network.id)
			this.orderState = {
				marketFillable: true,
				availableMakerAmount: this.parsedOrder.makerAssetAmount,
				availableTakerAmount: this.parsedOrder.takerAssetAmount
			}
			this.loopJob = null
			if (this.viewonly) {
				console.log('viewonly')
				return
			}
			if (this.initialorderstatechecking || this.continuousorderstatechecking) {
				await this.updateAssetBalance()
				await this.checkOrderState()
			}
			if (this.continuousorderstatechecking) {
				this.loopJob = setInterval(this.checkOrderState, loopJobInternval)
			}
		},
		formatAmountNumber: formatAmountNumber,
		formatAllowanceNumber: formatAllowanceNumber,
		async updateAssetBalance () {
			const makerAsseetBalance = this.account.balanceByAddress[this.parsedOrder.makerAssetAddress]
			const makerAsseetAllowance = this.account.allowanceByAddress[this.parsedOrder.makerAssetAddress]
			// console.log('makerAsseetBalance', makerAsseetBalance)
			// console.log('makerAsseetAllowance', makerAsseetAllowance)
			if (makerAsseetBalance === undefined || makerAsseetBalance === null || makerAsseetAllowance === undefined || makerAsseetAllowance === null) {
				await this.updateAccountForToken(this.parsedOrder.metaData.makerAsset)
			}

			const takerAsseetBalance = this.account.balanceByAddress[this.parsedOrder.takerAssetAddress]
			const takerAsseetAllowance = this.account.allowanceByAddress[this.parsedOrder.takerAssetAddress]
			// console.log('takerAsseetBalance', takerAsseetBalance)
			// console.log('takerAsseetAllowance', takerAsseetAllowance)
			if (takerAsseetBalance === undefined || takerAsseetBalance === null || takerAsseetAllowance === undefined || takerAsseetAllowance === null) {
				await this.updateAccountForToken(this.parsedOrder.metaData.takerAsset)
			}
		},
		async checkOrderState () {
			// console.log('######## loading order state #######')
			if (this.orderStateLoading) {
				return
			}
			this.orderStateLoading = true

			const orderState = await loadOrderState(
				this.parsedOrder.signedOrder,
				this.parsedOrder.metaData,
				this.myaddress,
				this.myaccount,
				this.contractWrappers
			)

			// console.log(orderState)
			this.orderState = { ...orderState }

			if (orderState.marketFillable === false) {
				this.stopLoopJob()
			}

			this.initialOrderStateLoadingDone = true
			this.orderStateLoading = false
		},

		async forwarderBuyERC20Token() {
			console.log('forwarderBuyERC20Token')
		},

		showCancelOrderModal () {
			const refKey = 'cancel-order-modal-' + this.parsedOrder.orderHash
			this.$refs[refKey].showModal()
		},

		hideCancelOrderModal () {
			const refKey = 'cancel-order-modal-' + this.parsedOrder.orderHash
			this.$refs[refKey].hideModal()
		},

		async handleCancelOrderFailure () {
			this.cancelOrderLoading = false
		},

		handleCancelOrderLoading () {
			this.cancelOrderLoading = true
		},

		async handleCancelOrderDone () {
			await this.checkOrderState()
			this.cancelOrderLoading = false
		},

		showDeleteOrderModal () {
			const refKey = 'delete-order-modal-' + this.parsedOrder.orderHash
			this.$refs[refKey].showModal()
		},

		hideDeleteOrderModal () {
			const refKey = 'delete-order-modal-' + this.parsedOrder.orderHash
			this.$refs[refKey].hideModal()
		},

		emitDeleteOrderComplete() {
			this.$emit('delete-order-complete', this.parsedOrder.orderHash)
		},

		stopLoopJob () {
			// console.log('stopLoopJob')
			if (this.loopJob) {
				clearInterval(this.loopJob)
			}
		},

		emitFillOrderCloseModal () {
			this.$emit('fill-order-form-close-modal')
		},

	},
	created () {
		this.init()
	},
	beforeDestroy () {
		// console.log('SimpleOrderSummary beforeDestroy')
		this.stopLoopJob()
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.order-summary {
	font-size: 14px;
}
.order-section {
	text-align: left;
	margin: 15px auto;
	padding: 20px 25px 20px 25px;
	border: 1px solid #dedede;
	/*min-width: 500px;*/
	max-width: 500px;
	background-color: #f9f9f9;
	/*background-color: #FFF;*/
}
.theme--dark .order-section {
	background-color: #353535;
	color: #EDEDED;
}
.order-section-plain {
	text-align: left;
	margin: 10px auto;
	/*min-width: 450px;*/
	max-width: 500px;
}
.order-fillable-errors ul {
	list-style-type: none;
	margin: 0;
	padding: 0;
}
.order-fillable-errors li {
	background-color: #e7c9c2;
	margin-bottom: 2px;
	padding: 3px 10px;
	border-radius: 1px;
	color: #2d2d2d;
	font-size: 12.5px;
}
.theme--dark .order-fillable-errors li {
	background-color: #3f2a2a;
	color: #CCC;
}
.own-order-label {
	display: inline-block;
	background-color: #ccf0db;
	padding: 3px 10px;
	border-radius: 1px;
}
.theme--dark .own-order-label {
	color: #444;
}
</style>
